import React from 'react'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { Container } from '../components/ui/Container'
import { Button } from '../components/ui/Button'
import { EnhancedFeatureCard, FeatureGrid } from '../components/EnhancedFeatureCard'
import { UpcomingFeaturesTimeline } from '../components/UpcomingFeaturesTimeline'
import TechnicalInfrastructureSection from '../components/pitch-deck/TechnicalInfrastructureSection'
import DetailedPillarsSection from '../components/pitch-deck/DetailedPillarsSection'
import DefensibilityMoatSection from '../components/pitch-deck/DefensibilityMoatSection'
import FutureExpansionSection from '../components/pitch-deck/FutureExpansionSection'
import {
  Calendar,
  Heart,
  Microscope,
  ArrowRight,
  BarChart3,
  HeartPulse,
  Dna,
  Users,
  Download
} from 'lucide-react'

import PageLayout from '../components/layout/PageLayout'
import AIVisual from '../components/ui/AIVisual'

const Features: React.FC = () => {

  const dashboardFeatures = [
    {
      icon: BarChart3,
      title: "Interactive Dashboards",
      description: "Beautiful, easy-to-understand visualizations of your health data with customizable views."
    },
    {
      icon: HeartPulse,
      title: "Real-time Monitoring",
      description: "Track key health metrics in real-time with wearable device integration."
    },
    {
      icon: Dna,
      title: "Genetic Integration",
      description: "Combine biomarker data with genetic information for comprehensive health insights."
    },
    {
      icon: Users,
      title: "Health Coaching",
      description: "Access to certified health coaches for personalized guidance and support."
    }
  ]

  const testingProcess = [
    {
      step: 1,
      title: "Choose Testing Option",
      description: "Select a partner location appointment or an at-home visit with a certified professional.",
      icon: Calendar
    },
    {
      step: 2,
      title: "Schedule & Prepare",
      description: "Book your slot and receive simple prep guidelines for a smooth experience.",
      icon: Download
    },
    {
      step: 3,
      title: "Professional Sample Collection",
      description: "Get your sample collected at the clinic or at home by a licensed professional.",
      icon: Heart
    },
    {
      step: 4,
      title: "Lab Analysis & Results",
      description: "CLIA-certified labs analyze your biomarkers and deliver personalized insights.",
      icon: Microscope
    }
  ]

  const biomarkerCategories = [
    {
      category: "Hormones",
      markers: ["Testosterone", "Estrogen", "Progesterone", "Cortisol", "DHEA-S", "Thyroid Panel"],
      importance: "Essential for energy, mood, metabolism, and reproductive health"
    },
    {
      category: "Nutrients",
      markers: ["Vitamin D", "B12", "Folate", "Iron", "Magnesium", "Zinc", "Copper"],
      importance: "Critical for immune function, energy production, and cellular health"
    },
    {
      category: "Metabolic",
      markers: ["Glucose", "HbA1c", "Insulin", "Cholesterol Panel", "Triglycerides"],
      importance: "Key indicators of metabolic health and diabetes risk"
    },
    {
      category: "Cardiovascular",
      markers: ["ApoB", "Lp(a)", "CRP", "Homocysteine", "NT-proBNP"],
      importance: "Advanced markers for heart disease prevention"
    },
    {
      category: "Liver & Kidney",
      markers: ["ALT", "AST", "GGT", "Creatinine", "BUN", "eGFR"],
      importance: "Essential for detoxification and waste elimination"
    },
    {
      category: "Longevity",
      markers: ["GlycanAge", "Telomere Length", "Inflammatory Markers", "Oxidative Stress"],
      importance: "Cutting-edge markers for biological age and longevity"
    }
  ]

  return (
    <PageLayout>
      {/* Technical Infrastructure - The Bio-Loop */}
      <TechnicalInfrastructureSection />

      {/* Detailed Pillars Technical Deep-Dive */}
      <DetailedPillarsSection />

      {/* Defensibility Moat Section */}
      <DefensibilityMoatSection />

      {/* Future Expansion - AI Health Kiosks */}
      <FutureExpansionSection />


      {/* Dashboard Features */}
      <section className="section-padding bg-gradient-to-br from-theme-surface to-theme-elevated relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 bg-gradient-to-r from-theme-primary/5 via-transparent to-theme-secondary/5" />
        
        <Container className="relative">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <motion.h2 
              className="text-4xl md:text-5xl font-display font-bold gradient-heading mb-4"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
            >
              Powerful Health Dashboard
            </motion.h2>
            <motion.p 
              className="text-xl text-theme-text-secondary max-w-3xl mx-auto leading-relaxed"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
            >
              Our intuitive dashboard makes complex health data easy to understand and act upon.
            </motion.p>
          </motion.div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
            >
              <FeatureGrid columns={2}>
                {dashboardFeatures.map((feature, index) => (
                  <EnhancedFeatureCard
                    key={feature.title}
                    icon={feature.icon}
                    title={feature.title}
                    description={feature.description}
                    color="from-theme-primary to-theme-secondary"
                    index={index}
                    variant="glass"
                  />
                ))}
              </FeatureGrid>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              className="relative"
            >
              <AIVisual
                src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&q=80&w=1200"
                alt="BioTune Health Dashboard"
                fallbackType="tech"
                className="rounded-3xl shadow-2xl border-4 border-white/10 transform hover:scale-[1.02] transition-transform duration-500"
              />
            </motion.div>
          </div>
        </Container>
      </section>

      {/* Testing Process */}
      <section className="section-padding bg-gradient-to-b from-theme-background to-theme-surface relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 bg-gradient-to-r from-theme-primary/5 via-transparent to-theme-secondary/5" />
        
        <Container className="relative">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <motion.h2 
              className="text-4xl md:text-5xl font-display font-bold gradient-heading mb-4"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
            >
              Simple Testing Process
            </motion.h2>
            <motion.p 
              className="text-xl text-theme-text-secondary max-w-3xl mx-auto leading-relaxed"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
            >
              Get comprehensive health insights in just a few easy steps.
            </motion.p>
          </motion.div>
          
          <FeatureGrid columns={4}>
            {testingProcess.map((step, index) => (
              <EnhancedFeatureCard
                key={step.title}
                icon={step.icon}
                title={step.title}
                description={step.description}
                color="from-theme-primary to-theme-secondary"
                index={index}
                variant="minimal"
                stepNumber={step.step}
              />
            ))}
          </FeatureGrid>
        </Container>
      </section>

      {/* Biomarker Categories */}
      <section className="section-padding bg-gradient-to-br from-theme-surface to-theme-elevated relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 bg-gradient-to-r from-theme-primary/5 via-transparent to-theme-secondary/5" />
        
        <Container className="relative">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <motion.h2 
              className="text-4xl md:text-5xl font-display font-bold gradient-heading mb-4"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
            >
              Comprehensive Biomarker Analysis
            </motion.h2>
            <motion.p 
              className="text-xl text-theme-text-secondary max-w-3xl mx-auto leading-relaxed"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
            >
              We analyze the most important biomarkers for optimal health and longevity.
            </motion.p>
          </motion.div>
          
          <FeatureGrid columns={3}>
            {biomarkerCategories.map((category, index) => (
              <EnhancedFeatureCard
                key={category.category}
                icon={Microscope}
                title={category.category}
                description={category.importance}
                color="from-theme-primary to-theme-secondary"
                index={index}
                variant="gradient"
                markers={category.markers}
              />
            ))}
          </FeatureGrid>
        </Container>
      </section>

      {/* Upcoming Features Timeline */}
      <UpcomingFeaturesTimeline />

      {/* CTA Section */}
      <section className="section-padding bg-theme-primary relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 bg-gradient-to-r from-white/10 via-transparent to-white/10" />
        
        <Container className="relative">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="text-center"
          >
            <motion.h2 
              className="text-4xl md:text-5xl font-display font-bold mb-6 text-theme-text-inverse"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              Ready to Optimize Your Health?
            </motion.h2>
            <motion.p 
              className="text-xl mb-8 max-w-3xl mx-auto text-theme-text-inverse/90 leading-relaxed"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              Join thousands of people who are taking control of their health with BioTune's advanced analytics.
            </motion.p>
            <motion.div 
              className="flex flex-col sm:flex-row gap-4 justify-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
            >
              <Link to="/auth/signup">
                <Button size="lg" className="bg-gradient-to-r from-theme-primary to-theme-secondary text-white hover:from-theme-primary hover:to-theme-secondary shadow-xl hover:shadow-2xl transition-all duration-300 group">
                  <span className="flex items-center gap-2">
                    Start Your Journey
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </span>
                </Button>
              </Link>
              <Link to="/contact">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-theme-primary shadow-xl transition-all duration-300">
                  Speak with an Expert
                </Button>
              </Link>
            </motion.div>
          </motion.div>
        </Container>
      </section>
    </PageLayout>
  )
}

export default Features
